import * as MP4Box from "mp4box";
import {DataStream} from "mp4box";

function makeDescription(src) {
    // ① 입력을 Uint8Array로 통일
    const avcc = src instanceof Uint8Array
        ? new Uint8Array(src)          // 복사본
        : Uint8Array.from(
            Array.from({length: Object.keys(src).length},
                (_, i) => src[i]));

    const dv = new DataView(avcc.buffer);

    /*  avcC 구조
        0: configurationVersion
        1: AVCProfileIndication
        2: profile_compatibility
        3: AVCLevelIndication
        4: reserved(6) + lengthSizeMinusOne
        5: reserved(3) + numOfSPS
        6‑7: sequenceParameterSetLength   ← 여기부터 Little‑Endian으로 잘못
        8.. : SPS NALU
        …   numOfPPS(1) + pictureParameterSetLength(2) + PPS NALU
    */

    // ② SPS 길이(LE → BE) 고치기
    let off = 6;
    const spsLen = dv.getUint16(off, /*littleEndian=*/ true);
    dv.setUint16(off, spsLen, /*littleEndian=*/ false);
    off += 2 + spsLen;           // SPS 본문 건너뜀

    // ③ numOfPPS 건너뜀
    off += 1;

    // ④ PPS 길이(LE → BE) 고치기
    const ppsLen = dv.getUint16(off, true);
    dv.setUint16(off, ppsLen, false);

    return avcc.buffer;          // WebCodecs description 완성
}

function createAsyncQueue() {
    const queue = [];
    let resolveNext = null;

    return {
        enqueue(item) {
            if (resolveNext) {
                resolveNext(item);
                resolveNext = null;
            } else {
                queue.push(item);
            }
        },
        dequeue() {
            return new Promise((resolve) => {
                if (queue.length > 0) {
                    resolve(queue.shift());
                } else {
                    resolveNext = resolve;
                }
            });
        },
        isEmpty() {
            return queue.length === 0 && !resolveNext;
        },
        length() {
            return queue.length + (resolveNext ? 1 : 0);
        },
    };
}

export async function extractFramesFromMP4(url) {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.statusText}`);
    }

    const buf = await response.arrayBuffer();
    const queue = createAsyncQueue();

    return new Promise(async (resolve, reject) => {
        const mp4boxFile = MP4Box.createFile();

        let videoTrack, decoder;

        mp4boxFile.onReady = (info) => {
            console.log("MP4Box file ready:", info);

            videoTrack = info.videoTracks[0];

            if (!videoTrack) {
                return reject(new Error("No video track found in the MP4 file."));
            }

            const trak = mp4boxFile.getTrackById(videoTrack.id);   // = moov.traks[i]

            const entry = trak.mdia.minf.stbl.stsd.entries[0];
            const cfgBox = entry.avcC ?? entry.hvcC ?? entry.vpcC ?? entry.av1C;

            const ds = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
            cfgBox.write(ds);

            const description = makeDescription(new Uint8Array(ds.buffer.slice(8))); // <-- SPS·PPS·etc.

            console.log("decoder description:", description);

            decoder = new window.VideoDecoder({
                output(frame) {
                    // console.log("Decoded frame:", frame);
                    // console.log("num frame:", queue.length());
                    queue.enqueue(frame);
                },
                error(e) {
                    console.error(e);
                }
            });

            decoder.configure({
                codec: videoTrack.codec,            // "avc1.640020" 같은 값
                codedWidth: videoTrack.track_width,
                codedHeight: videoTrack.track_height,
                description                           // 방금 만든 extradata
            });

            // mp4box에 “이 트랙의 sample을 주세요”
            mp4boxFile.setExtractionOptions(videoTrack.id, null, {nbSamples: 1, rap: true});
            mp4boxFile.start();

            resolve(queue);
        };

        mp4boxFile.onSamples = (id, user, samples) => {
            console.log("Received samples for track ID:", id, "Samples count:", samples.length);

            for (const s of samples) {
                const chunk = new window.EncodedVideoChunk({
                    type: s.is_sync ? 'key' : 'delta',
                    timestamp: s.cts,          // μs 단위
                    duration: s.duration,
                    data: s.data               // Uint8Array
                });
                decoder.decode(chunk);
            }
        };

        buf.fileStart = 0;               // 필수 프로퍼티!
        mp4boxFile.appendBuffer(buf);
        mp4boxFile.flush();              // 전부 들어갔음을 알림

        console.log("MP4Box file appended and flushed.");

        await decoder.flush();
        console.log("Decoder flushed, all frames processed.");

        decoder.close();
        console.log("Decoder closed, no more frames will be processed.");
        queue.enqueue(null); // signal end of frames
    })
}
